import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface TopProductsCardProps {
  products: any[];
  loading: boolean;
  onViewAll: () => void;
  semanticColors?: unknown;
}

const formatCount = (count: number) => {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count ?? 0);
};

/** The API has answered with several shapes over time; read them all. */
const getViews = (product: any) =>
  product?.views ?? product?.totalViews ?? product?.count ?? product?.productPageViews ?? 0;

const getUsers = (product: any) =>
  product?.uniqueUsers ?? product?.users ?? product?.uniqueVisitors ?? 0;

const getImage = (product: any) =>
  product?.thumbnailPath ?? product?.image ?? product?.visualization?.thumbnailPath ?? product?.galleryImages?.[0];

export const TopProductsCard = ({ products, loading, onViewAll }: TopProductsCardProps) => {
  const { t } = useTranslation();
  const safeProducts = Array.isArray(products) ? products : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">{t('PAGES.DASHBOARD.TOP_PRODUCTS')}</CardTitle>
        <CardAction>
          <Button variant="ghost" size="sm" onClick={onViewAll} className="h-7 text-xs">
            {t('PAGES.DASHBOARD.VIEW_ALL')}
          </Button>
        </CardAction>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-11" />
            ))}
          </div>
        ) : !safeProducts.length ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('PAGES.DASHBOARD.NO_PRODUCT_DATA')}</p>
        ) : (
          <div className="flex flex-col">
            {safeProducts.map((product: any, index: number) => {
              const image = getImage(product);
              return (
                <div
                  key={product?.uuid ?? product?.id ?? index}
                  className="-mx-2 flex items-center gap-3 px-2 py-2"
                >
                  <span className="w-4 shrink-0 text-xs tabular-nums text-muted-foreground">{index + 1}</span>

                  {image ? (
                    <img src={image} alt="" className="size-9 shrink-0 border object-cover" />
                  ) : (
                    <span className="size-9 shrink-0 border bg-muted" />
                  )}

                  <span className="min-w-0 flex-1 truncate text-sm">
                    {product?.name ?? product?.productName ?? '—'}
                  </span>

                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {formatCount(getViews(product))} · {formatCount(getUsers(product))}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
